import React, { useState, useEffect } from 'react';
import { stockMovementService } from '../../api/stockMovementService';
import { productService } from '../../api/productService';
import './StockMovementForm.css';

function StockMovementForm({ onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    product_id: '',
    movement_type: 'in',
    quantity: 1,
    unit_price: '',
    reference: '',
  });
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts([]);
      setShowProductList(false);
    } else {
      const filtered = products.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
      setShowProductList(true);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      const response = await productService.getProducts({ per_page: 10 });
      setProducts(response.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData((prev) => ({
      ...prev,
      product_id: product.id,
      unit_price: formData.movement_type === 'in' ? '' : product.price,
    }));
    setSearchTerm(product.name);
    setShowProductList(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If movement type changes, update unit price
    if (name === 'movement_type' && selectedProduct) {
      setFormData((prev) => ({
        ...prev,
        unit_price: value === 'in' ? '' : selectedProduct.price,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.product_id) {
      setError('Veuillez sélectionner un produit');
      return;
    }

    if (!formData.unit_price || parseFloat(formData.unit_price) <= 0) {
      setError('Veuillez entrer un prix unitaire valide');
      return;
    }

    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      setError('Veuillez entrer une quantité valide');
      return;
    }

    // Check if removing more stock than available
    if (formData.movement_type !== 'in' && selectedProduct) {
      if (parseInt(formData.quantity) > selectedProduct.stock_quantity) {
        setError(`Impossible de retirer ${formData.quantity} articles. Seulement ${selectedProduct.stock_quantity} en stock.`);
        return;
      }
    }

    setLoading(true);
    try {
      await stockMovementService.createStockMovement(formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la création du mouvement de stock');
    } finally {
      setLoading(false);
    }
  };

  const getTotalAmount = () => {
    const qty = parseFloat(formData.quantity) || 0;
    const price = parseFloat(formData.unit_price) || 0;
    return (qty * price).toFixed(2);
  };

  return (
    <form onSubmit={handleSubmit} className="stock-movement-form">
      {error && (
        <div className="error-message" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div className="form-section">
        <h3>Détails du mouvement</h3>

        <div className="form-group">
          <label>Type de mouvement *</label>
          <select
            name="movement_type"
            value={formData.movement_type}
            onChange={handleChange}
            required
          >
            <option value="in">Entrée de stock (Ajouter à l'inventaire)</option>
            <option value="sale">Vente (Retirer de l'inventaire)</option>
            <option value="gift">Cadeau (Retirer de l'inventaire)</option>
            <option value="destruction">Destruction (Retirer de l'inventaire)</option>
          </select>
          <p className="field-hint">
            {formData.movement_type === 'in' ? 'Ajout de stock à l\'inventaire' : 'Retrait de stock de l\'inventaire'}
          </p>
        </div>

        <div className="form-group">
          <label>Produit *</label>
          <div className="product-search-wrapper">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm && setShowProductList(true)}
              placeholder="Rechercher par nom de produit, SKU ou code-barres..."
              required
              autoComplete="off"
            />
            {showProductList && filteredProducts.length > 0 && (
              <div className="product-dropdown">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-item"
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="product-item-name">{product.name}</div>
                    <div className="product-item-details">
                      SKU: {product.sku || 'N/A'} | Stock: {product.stock_quantity} | Prix: {parseFloat(product.price).toFixed(2)} DH
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedProduct && (
            <div className="selected-product-info">
              <strong>Sélectionné :</strong> {selectedProduct.name}
              <br />
              <strong>Stock actuel :</strong> {selectedProduct.stock_quantity} unités
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Quantité *</label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              required
              min="1"
              step="1"
            />
            {selectedProduct && formData.movement_type !== 'in' && (
              <p className="field-hint">
                Disponible : {selectedProduct.stock_quantity} unités
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Prix unitaire (DH) *</label>
            <input
              type="number"
              name="unit_price"
              value={formData.unit_price}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              placeholder="0.00"
            />
            <p className="field-hint">
              {formData.movement_type === 'in' ? 'Prix d\'achat/coût' : 'Prix de vente'}
            </p>
          </div>
        </div>

        <div className="form-group">
          <label>Référence</label>
          <input
            type="text"
            name="reference"
            value={formData.reference}
            onChange={handleChange}
            placeholder="Numéro de facture, ID de commande, etc."
            maxLength={100}
          />
          <p className="field-hint">Numéro de référence optionnel pour le suivi</p>
        </div>

        {formData.quantity && formData.unit_price && (
          <div className="total-amount-display">
            <strong>Montant total :</strong> {getTotalAmount()} DH
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Création...' : 'Créer le mouvement'}
        </button>
      </div>
    </form>
  );
}

export default StockMovementForm;
